import { Hint } from '../../components/ui/hint';
import { useEffect, useState } from 'react';
import { api, errorLine } from '../../api/client';
import type { ShadowCoverage } from '../../api/types';
import { formatMoney } from '../../lib/format';
import { formatCredits } from '../../lib/credits';
const names: Record<string, string> = {
  quick_search: 'Quick Search',
  research: 'Research',
  prose_refresh: 'Prose refresh',
  org_lookup: 'Organization lookup',
  report_generate: 'Report',
  report_update: 'Report update',
  report_refresh: 'Report refresh',
  report_revise: 'Report revision',
  event_research: 'Event research',
  event_scan: 'Event scan',
  note: 'Note reading',
  import: 'Import',
};
const cost = (value: number | null) => (value === null ? 'Not reported' : formatMoney(value));
export function ShadowCoverageTable({ view }: { view: ShadowCoverage }) {
  return (
    <>
      <div className="credit-history">
        <table className="table">
          <thead>
            <tr>
              <th>Workload</th>
              <th>Operations</th>
              <th>Shadow consumption</th>
              <th>Recorded provider cost</th>
            </tr>
          </thead>
          <tbody>
            {view.rows.map((r, i) => (
              <tr key={i}>
                <td>
                  {names[r.operation_kind] ?? r.operation_kind}
                  <div>
                    {[
                      r.depth === 'travel_brief' ? 'Travel brief' : r.depth,
                      r.origin,
                      r.size_band ? `${r.size_band} characters` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                  <details className="row-detail">
                    <summary>Pricing policy</summary>
                    {r.policy_version}
                  </details>
                </td>
                <td>
                  {r.operations} tracked
                  <div>
                    {r.completed} complete · {r.pending} pending
                  </div>
                  <details className="row-detail">
                    <summary>Other outcomes</summary>
                    {r.failed} failed · {r.cancelled} cancelled · {r.undone} undone · {r.superseded}{' '}
                    superseded · {r.removed_unfinished} removed unfinished
                  </details>
                </td>
                <td>
                  {r.unpriced_operations === r.operations
                    ? 'Unpriced'
                    : `${formatCredits(r.known_completed_units)} credits`}
                  <div>{r.unpriced_operations} operations without a price</div>
                  {r.known_pending_units > 0 && (
                    <div>{formatCredits(r.known_pending_units)} potential credits pending</div>
                  )}
                </td>
                <td>
                  {cost(
                    r.model_micro_usd === null && r.external_micro_usd === null
                      ? null
                      : (r.model_micro_usd ?? 0) + (r.external_micro_usd ?? 0),
                  )}
                  <div>{r.provider_attempts} provider attempts</div>
                  {r.model_events_without_receipts > 0 && (
                    <div>
                      {r.model_events_without_receipts} additional model usage records (may include
                      aggregate or partial estimates)
                    </div>
                  )}
                  {r.operations_without_cost_records > 0 && (
                    <div>
                      {r.operations_without_cost_records} operations without cost records; their
                      cost is unknown.
                    </div>
                  )}
                  {!!(
                    r.incomplete_attempts ||
                    r.unpriced_attempts ||
                    r.pending_pipeline_reports ||
                    r.fallback_priced_records
                  ) && (
                    <div>
                      Cost caveats: {r.incomplete_attempts} incomplete · {r.unpriced_attempts}{' '}
                      unpriced · {r.pending_pipeline_reports} pending reports ·{' '}
                      {r.fallback_priced_records} fallback estimates
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!view.rows.length && <p>No operations captured yet.</p>}
      {(view.outside_observations.provider_attempts > 0 ||
        view.outside_observations.model_events_without_receipts > 0) && (
        <p className="field-note">
          Outside captured operations: {view.outside_observations.provider_attempts} provider
          attempts, {view.outside_observations.model_events_without_receipts ?? 0} additional model
          usage records, {cost(view.outside_observations.known_micro_usd)} known cost,{' '}
          {view.outside_observations.unpriced_attempts} unpriced. Includes historical or disabled
          tracking periods and rolled-back admissions.
        </p>
      )}
    </>
  );
}
export function ShadowCoveragePanel({ orgId, revision }: { orgId: string; revision?: number }) {
  const [view, setView] = useState<ShadowCoverage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    api
      .shadowCoverage(orgId)
      .then((v) => {
        if (live) setView(v);
      })
      .catch((e) => {
        if (live) setError(errorLine(e));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [orgId, revision, reload]);
  return (
    <div className="shadow-coverage">
      <h4>All operation coverage</h4>
      <Hint label="About operation coverage">
        All-time captured activity. Notes own their costs separately from the parent import or run.
      </Hint>
      {error && <p role="alert">{error}</p>}
      {view ? <ShadowCoverageTable view={view} /> : !error && <p>Loading operation coverage…</p>}
      <div className="form-actions">
        <button
          className="btn"
          type="button"
          disabled={loading}
          onClick={() => setReload((n) => n + 1)}
        >
          {loading ? 'Refreshing…' : 'Refresh coverage'}
        </button>
        <button
          className="btn"
          type="button"
          disabled={!view || loading || !!error}
          onClick={() => {
            const url = URL.createObjectURL(
              new Blob([JSON.stringify(view, null, 2)], { type: 'application/json' }),
            );
            const a = document.createElement('a');
            a.href = url;
            a.download = 'shadow-operation-coverage.json';
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          }}
        >
          Export coverage
        </button>
      </div>
      <Hint label="Cost coverage limits">
        Costs are estimates, not reconciled invoices. Unknown costs are not zero. Import completion
        does not mean every note was read.
      </Hint>
    </div>
  );
}
